// ─── Tipos ───

export interface UbicacionDetectada {
  pais: string;
  paisCodigo: string;
  estado: string;
  ciudad: string;
  lat: number;
  lng: number;
}

export interface RadioCercana {
  id: string;
  nombre: string;
  frecuencia: string;
  ciudad: string;
  estado: string;
  pais: string;
  paisCodigo: string;
  logoUrl: string;
  imagenUrl: string;
  categoria: string;
  idioma: string;
  distancia: number;
  oyentes: number;
  locutorPrincipal: string;
  locutorAvatarUrl: string;
  estaEnVivo: boolean;
  enTendencia: boolean;
  rating: number;
  tags: string[];
  descripcion: string;
}

export interface EventoCercano {
  id: string;
  titulo: string;
  tipo: string;
  ciudad: string;
  estado: string;
  pais: string;
  paisCodigo: string;
  imagenUrl: string;
  radioNombre: string;
  radioLogoUrl: string;
  fecha: string;
  hora: string;
  distancia: number;
  participantes: number;
  esGratuito: boolean;
  descripcion: string;
  tags: string[];
}

export interface LocutorCercano {
  id: string;
  nombre: string;
  ciudad: string;
  estado: string;
  pais: string;
  paisCodigo: string;
  avatarUrl: string;
  imagenUrl: string;
  radioNombre: string;
  radioFrecuencia: string;
  radioLogoUrl: string;
  distancia: number;
  seguidores: number;
  estaEnVivo: boolean;
  especialidad: string;
  idioma: string;
  bio: string;
  tags: string[];
}

export interface OpcionDistancia {
  id: string;
  label: string;
  minKm: number;
  maxKm: number;
}

export interface OpcionIdioma {
  id: string;
  label: string;
}

export interface OpcionCategoria {
  id: string;
  label: string;
  icono: string;
}

// ─── Reglas del Sistema ───

export interface ReglaRadiosCercanas {
  id: number;
  titulo: string;
  descripcion: string;
  icono: string;
  gradiente: string;
}

export const reglasRadiosCercanas: ReglaRadiosCercanas[] = [
  {
    id: 1,
    titulo: 'Siempre Permitir Cambiar Ubicación Manualmente',
    descripcion: 'El oyente tiene control total sobre su ubicación. Puede buscar radios en cualquier ciudad del mundo con un solo clic. La geolocalización automática es una sugerencia, nunca una imposición.',
    icono: 'ri-map-pin-user-line',
    gradiente: 'from-rose-500 to-amber-500',
  },
  {
    id: 2,
    titulo: 'Todo se Ordena por Distancia Real',
    descripcion: 'Radios, eventos y locutores se ordenan del más cercano al más lejano. La distancia se calcula en kilómetros desde el centro de la ciudad seleccionada. Sin atajos ni manipulaciones.',
    icono: 'ri-gps-line',
    gradiente: 'from-emerald-500 to-teal-500',
  },
  {
    id: 3,
    titulo: 'La Cercanía Física Impulsa Descubrimiento Local',
    descripcion: 'Las radios que transmiten desde tu ciudad aparecen primero. Los eventos que ocurren en tu barrio tienen prioridad. Los locutores que viven cerca se muestran destacados. Lo local siempre gana.',
    icono: 'ri-building-line',
    gradiente: 'from-violet-500 to-purple-500',
  },
  {
    id: 4,
    titulo: 'Filtros que Refinan sin Frustrar',
    descripcion: 'Distancia, idioma y categoría trabajan juntos. Podés buscar radios de rock en español a menos de 10km. O eventos gratuitos en tu ciudad. Los filtros suman, no restan — siempre hay resultados.',
    icono: 'ri-filter-3-line',
    gradiente: 'from-amber-500 to-orange-500',
  },
];

// ─── Ubicaciones Detectables ───

export const ubicacionesDetectables: UbicacionDetectada[] = [
  { pais: 'México', paisCodigo: 'MX', estado: 'Ciudad de México', ciudad: 'CDMX', lat: 19.4326, lng: -99.1332 },
  { pais: 'Argentina', paisCodigo: 'AR', estado: 'Buenos Aires', ciudad: 'CABA', lat: -34.6037, lng: -58.3816 },
  { pais: 'España', paisCodigo: 'ES', estado: 'Comunidad de Madrid', ciudad: 'Madrid', lat: 40.4168, lng: -3.7038 },
  { pais: 'Colombia', paisCodigo: 'CO', estado: 'Bogotá D.C.', ciudad: 'Bogotá', lat: 4.7110, lng: -74.0721 },
  { pais: 'Costa Rica', paisCodigo: 'CR', estado: 'San José', ciudad: 'San José', lat: 9.9281, lng: -84.0907 },
];

export const ubicacionPorDefecto: UbicacionDetectada = ubicacionesDetectables[0];

// ─── Opciones de Filtro ───

export const opcionesDistancia: OpcionDistancia[] = [
  { id: 'todas', label: 'Todas', minKm: 0, maxKm: 99999 },
  { id: '5km', label: '< 5 km', minKm: 0, maxKm: 5 },
  { id: '10km', label: '< 10 km', minKm: 0, maxKm: 10 },
  { id: '25km', label: '< 25 km', minKm: 0, maxKm: 25 },
  { id: '50km', label: '< 50 km', minKm: 0, maxKm: 50 },
  { id: '100km', label: '< 100 km', minKm: 0, maxKm: 100 },
];

export const opcionesIdioma: OpcionIdioma[] = [
  { id: 'todos', label: 'Todos' },
  { id: 'es', label: 'Español' },
  { id: 'en', label: 'English' },
  { id: 'pt', label: 'Portugués' },
];

export const opcionesCategoria: OpcionCategoria[] = [
  { id: 'todas', label: 'Todas', icono: 'ri-global-line' },
  { id: 'rock', label: 'Rock', icono: 'ri-flashlight-line' },
  { id: 'pop', label: 'Pop', icono: 'ri-music-line' },
  { id: 'electronica', label: 'Electrónica', icono: 'ri-headphone-line' },
  { id: 'regional', label: 'Regional', icono: 'ri-earth-line' },
  { id: 'noticias', label: 'Noticias', icono: 'ri-newspaper-line' },
  { id: 'deportiva', label: 'Deportiva', icono: 'ri-football-line' },
  { id: 'comunitaria', label: 'Comunitaria', icono: 'ri-group-line' },
  { id: 'cultural', label: 'Cultural', icono: 'ri-palette-line' },
];

// ─── Radios Cercanas ───

export const radiosCercanas: RadioCercana[] = [
  // ── México / CDMX ──
  {
    id: 'rc-radio-01',
    nombre: 'La Mexicana FM',
    frecuencia: '98.5 RFW',
    ciudad: 'CDMX',
    estado: 'Ciudad de México',
    pais: 'México',
    paisCodigo: 'MX',
    logoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20radio%20station%20logo%20with%20traditional%20serape%20colors%20red%20green%20white%2C%20circular%20design%2C%20broadcast%20emblem%20with%20mic%2C%20warm%20vibrant%20Mexican%20aesthetic%2C%20vector%20flat%20design%20isolated%20on%20transparent&width=200&height=200&seq=rc-radio-logo-01&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Warm%20vibrant%20Mexican%20radio%20studio%20with%20colorful%20traditional%20textiles%20on%20walls%2C%20modern%20broadcast%20equipment%2C%20daylight%20streaming%20through%20windows%2C%20professional%20yet%20cultural%20atmosphere%2C%20editorial%20photography%20with%20warm%20tones&width=1600&height=600&seq=rc-radio-img-01&orientation=landscape',
    categoria: 'regional',
    idioma: 'es',
    distancia: 1.2,
    oyentes: 3420,
    locutorPrincipal: 'Adriana Mejía',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Warm%20friendly%20Mexican%20female%20radio%20host%20portrait%2C%20genuine%20smile%2C%20colorful%20blouse%2C%20professional%20headshot%20in%20studio%2C%20soft%20natural%20lighting&width=80&height=80&seq=rc-loc-avatar-01&orientation=squarish',
    estaEnVivo: true,
    enTendencia: true,
    rating: 4.8,
    tags: ['regional mexicano', 'cultura', 'tradición'],
    descripcion: 'La voz del México auténtico. Regional mexicano, cultura y tradición desde el corazón de la CDMX. Más de 10 años conectando a la comunidad con sus raíces musicales.',
  },
  {
    id: 'rc-radio-02',
    nombre: 'Estéreo Capital',
    frecuencia: '102.3 RFW',
    ciudad: 'CDMX',
    estado: 'Ciudad de México',
    pais: 'México',
    paisCodigo: 'MX',
    logoUrl: 'https://readdy.ai/api/search-image?query=Modern%20urban%20radio%20station%20logo%20with%20Mexico%20City%20skyline%20silhouette%2C%20bold%20graphic%20design%2C%20mustard%20yellow%20and%20charcoal%20colors%2C%20contemporary%20broadcast%20emblem%2C%20vector%20flat%20design&width=200&height=200&seq=rc-radio-logo-02&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Contemporary%20urban%20radio%20studio%20in%20Mexico%20City%20high-rise%2C%20floor%20to%20ceiling%20windows%20with%20cityscape%20view%2C%20modern%20minimalist%20broadcast%20setup%2C%20warm%20evening%20lights%2C%20sophisticated%20editorial%20photography&width=1600&height=600&seq=rc-radio-img-02&orientation=landscape',
    categoria: 'pop',
    idioma: 'es',
    distancia: 2.5,
    oyentes: 5670,
    locutorPrincipal: 'Diego Vargas',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Mexican%20male%20radio%20host%20portrait%2C%20stylish%20modern%20look%2C%20confident%20expression%2C%20urban%20professional%20headshot%2C%20warm%20studio%20lighting&width=80&height=80&seq=rc-loc-avatar-02&orientation=squarish',
    estaEnVivo: true,
    enTendencia: false,
    rating: 4.6,
    tags: ['pop', 'entrevistas', 'urbano'],
    descripcion: 'El sonido de la capital mexicana. Pop contemporáneo, entrevistas con artistas y la mejor cobertura de eventos en CDMX.',
  },
  {
    id: 'rc-radio-03',
    nombre: 'Radio Noticias 24/7',
    frecuencia: '88.1 RFW',
    ciudad: 'CDMX',
    estado: 'Ciudad de México',
    pais: 'México',
    paisCodigo: 'MX',
    logoUrl: 'https://readdy.ai/api/search-image?query=News%20radio%20station%20logo%20with%20bold%20typography%2C%20red%20and%20white%20colors%2C%20globe%20motif%2C%20professional%20broadcast%20news%20emblem%2C%20vector%20flat%20design%20isolated%20on%20transparent&width=200&height=200&seq=rc-radio-logo-03&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Professional%20news%20radio%20studio%20with%20multiple%20monitors%20showing%20news%20feeds%2C%20broadcast%20microphones%2C%20modern%20newsroom%20atmosphere%2C%20red%20accent%20lighting%2C%20editorial%20photography&width=1600&height=600&seq=rc-radio-img-03&orientation=landscape',
    categoria: 'noticias',
    idioma: 'es',
    distancia: 3.8,
    oyentes: 8930,
    locutorPrincipal: 'Carlos Mendoza',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Senior%20Mexican%20news%20anchor%20portrait%2C%20distinguished%20silver%20hair%2C%20professional%20suit%2C%20authoritative%20expression%2C%20studio%20lighting&width=80&height=80&seq=rc-loc-avatar-03&orientation=squarish',
    estaEnVivo: true,
    enTendencia: true,
    rating: 4.9,
    tags: ['noticias', 'información', 'actualidad'],
    descripcion: 'Información actualizada las 24 horas. Noticias locales de CDMX, nacionales e internacionales con el mejor equipo periodístico.',
  },
  {
    id: 'rc-radio-04',
    nombre: 'Rock Total CDMX',
    frecuencia: '95.7 RFW',
    ciudad: 'CDMX',
    estado: 'Ciudad de México',
    pais: 'México',
    paisCodigo: 'MX',
    logoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20with%20electric%20guitar%20and%20lightning%20bolt%2C%20black%20and%20red%20color%20scheme%2C%20bold%20edgy%20design%2C%20music%20broadcast%20emblem%2C%20vector%20flat%20design&width=200&height=200&seq=rc-radio-logo-04&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Edgy%20rock%20radio%20studio%20with%20guitar%20collection%20on%20wall%2C%20dark%20moody%20atmosphere%20with%20warm%20accent%20lights%2C%20vinyl%20records%2C%20concert%20posters%2C%20authentic%20rock%20broadcast%20environment&width=1600&height=600&seq=rc-radio-img-04&orientation=landscape',
    categoria: 'rock',
    idioma: 'es',
    distancia: 4.1,
    oyentes: 4510,
    locutorPrincipal: 'Javier Solís',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Mexican%20rock%20radio%20DJ%20portrait%2C%20long%20hair%2C%20leather%20jacket%2C%20cool%20rock%20vibe%2C%20moody%20studio%20lighting%2C%20authentic%20music%20personality&width=80&height=80&seq=rc-loc-avatar-04&orientation=squarish',
    estaEnVivo: false,
    enTendencia: false,
    rating: 4.5,
    tags: ['rock', 'metal', 'conciertos'],
    descripcion: 'Rock sin censura desde la capital. Clásicos, metal, indie y la escena under de CDMX. Tu dosis diaria de guitarras.',
  },
  {
    id: 'rc-radio-05',
    nombre: 'Electro Vibes MX',
    frecuencia: '107.1 RFW',
    ciudad: 'CDMX',
    estado: 'Ciudad de México',
    pais: 'México',
    paisCodigo: 'MX',
    logoUrl: 'https://readdy.ai/api/search-image?query=Electronic%20music%20radio%20station%20logo%20with%20neon%20pink%20and%20cyan%20colors%2C%20sound%20wave%20design%2C%20futuristic%20DJ%20emblem%2C%20vector%20flat%20design%20isolated%20on%20transparent&width=200&height=200&seq=rc-radio-logo-05&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Futuristic%20electronic%20music%20radio%20studio%20with%20neon%20pink%20and%20cyan%20accent%20lighting%2C%20DJ%20decks%2C%20LED%20panels%2C%20modern%20minimalist%20broadcast%20space%2C%20night%20club%20atmosphere&width=1600&height=600&seq=rc-radio-img-05&orientation=landscape',
    categoria: 'electronica',
    idioma: 'es',
    distancia: 5.2,
    oyentes: 2340,
    locutorPrincipal: 'DJ Phantom',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Mexican%20DJ%20portrait%2C%20modern%20urban%20style%2C%20headphones%20around%20neck%2C%20confident%20cool%20expression%2C%20neon%20lighting&width=80&height=80&seq=rc-loc-avatar-05&orientation=squarish',
    estaEnVivo: false,
    enTendencia: false,
    rating: 4.3,
    tags: ['electrónica', 'DJ sets', 'house'],
    descripcion: 'El pulso electrónico de la CDMX. House, techno, deep y los mejores DJ sets en vivo desde el estudio.',
  },

  // ── Argentina / CABA ──
  {
    id: 'rc-radio-06',
    nombre: 'FM Sol 94.70',
    frecuencia: '94.70 RFW',
    ciudad: 'CABA',
    estado: 'Buenos Aires',
    pais: 'Argentina',
    paisCodigo: 'AR',
    logoUrl: 'https://readdy.ai/api/search-image?query=Argentine%20radio%20station%20logo%20with%20sun%20motif%2C%20warm%20orange%20and%20yellow%20colors%2C%20friendly%20broadcast%20emblem%2C%20circular%20design%2C%20vector%20flat%20design%20isolated&width=200&height=200&seq=rc-radio-logo-06&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Bright%20warm%20Argentine%20radio%20studio%20in%20Buenos%20Aires%2C%20natural%20daylight%20streaming%20through%20large%20windows%2C%20friendly%20broadcast%20atmosphere%2C%20plants%20and%20warm%20wood%20accents%2C%20editorial%20photography&width=1600&height=600&seq=rc-radio-img-06&orientation=landscape',
    categoria: 'pop',
    idioma: 'es',
    distancia: 0.8,
    oyentes: 12800,
    locutorPrincipal: 'Martín Castellanos',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20Argentine%20male%20radio%20host%20portrait%2C%20friendly%20smile%2C%20casual%20professional%20look%2C%20warm%20natural%20lighting%2C%20approachable%20personality&width=80&height=80&seq=rc-loc-avatar-06&orientation=squarish',
    estaEnVivo: true,
    enTendencia: true,
    rating: 4.9,
    tags: ['pop', 'matutino', 'entretenimiento'],
    descripcion: 'El despertar de Buenos Aires. Noticias, música y el mejor entretenimiento matutino. Una institución de la radio argentina.',
  },
  {
    id: 'rc-radio-07',
    nombre: 'Rock Nacional BA',
    frecuencia: '101.5 RFW',
    ciudad: 'CABA',
    estado: 'Buenos Aires',
    pais: 'Argentina',
    paisCodigo: 'AR',
    logoUrl: 'https://readdy.ai/api/search-image?query=Argentine%20rock%20national%20radio%20logo%20with%20guitar%20and%20flag%20colors%2C%20sky%20blue%20and%20white%2C%20bold%20design%2C%20music%20broadcast%20emblem%2C%20vector%20flat&width=200&height=200&seq=rc-radio-logo-07&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Argentine%20rock%20radio%20studio%20in%20Buenos%20Aires%20with%20band%20posters%2C%20vinyl%20collection%2C%20warm%20amber%20lighting%2C%20authentic%20music%20atmosphere%2C%20guitars%20on%20wall%2C%20editorial%20photography&width=1600&height=600&seq=rc-radio-img-07&orientation=landscape',
    categoria: 'rock',
    idioma: 'es',
    distancia: 1.9,
    oyentes: 7840,
    locutorPrincipal: 'Andrés Castillo',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Argentine%20rock%20radio%20DJ%20portrait%2C%20musician%20vibe%2C%20long%20hair%2C%20black%20t-shirt%2C%20guitar%20pick%20necklace%2C%20cool%20authentic%20personality&width=80&height=80&seq=rc-loc-avatar-07&orientation=squarish',
    estaEnVivo: true,
    enTendencia: false,
    rating: 4.7,
    tags: ['rock nacional', 'argentina', 'clásicos'],
    descripcion: 'El hogar del rock argentino. Desde Spinetta hasta las bandas under del conurbano. Todo el rock que te hace vibrar.',
  },
  {
    id: 'rc-radio-08',
    nombre: 'Radio Deportiva Sur',
    frecuencia: '89.3 RFW',
    ciudad: 'CABA',
    estado: 'Buenos Aires',
    pais: 'Argentina',
    paisCodigo: 'AR',
    logoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20soccer%20ball%20and%20whistle%2C%20green%20and%20white%20colors%2C%20dynamic%20design%2C%20sports%20broadcast%20emblem%2C%20vector%20flat&width=200&height=200&seq=rc-radio-logo-08&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20sports%20radio%20studio%20with%20multiple%20TV%20screens%20showing%20live%20matches%2C%20soccer%20memorabilia%2C%20energetic%20broadcast%20atmosphere%2C%20green%20accent%20lighting&width=1600&height=600&seq=rc-radio-img-08&orientation=landscape',
    categoria: 'deportiva',
    idioma: 'es',
    distancia: 3.2,
    oyentes: 6120,
    locutorPrincipal: 'Roberto Díaz',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Energetic%20Argentine%20sports%20commentator%20portrait%2C%20passionate%20expression%2C%20casual%20sporty%20style%2C%20headset%20microphone%2C%20professional%20headshot&width=80&height=80&seq=rc-loc-avatar-08&orientation=squarish',
    estaEnVivo: true,
    enTendencia: true,
    rating: 4.8,
    tags: ['deportes', 'fútbol', 'análisis'],
    descripcion: 'La pasión del deporte argentino. Fútbol, rugby, tenis y todo lo que quema. Análisis sin filtro y la mejor cobertura.',
  },

  // ── España / Madrid ──
  {
    id: 'rc-radio-09',
    nombre: 'Onda Capital Madrid',
    frecuencia: '96.2 RFW',
    ciudad: 'Madrid',
    estado: 'Comunidad de Madrid',
    pais: 'España',
    paisCodigo: 'ES',
    logoUrl: 'https://readdy.ai/api/search-image?query=Spanish%20radio%20station%20logo%20with%20bear%20and%20strawberry%20tree%20Madrid%20motif%2C%20red%20and%20cream%20colors%2C%20elegant%20broadcast%20emblem%2C%20circular%20design%2C%20vector%20flat&width=200&height=200&seq=rc-radio-logo-09&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Elegant%20Spanish%20radio%20studio%20in%20Madrid%20with%20classic%20wood%20paneling%2C%20warm%20professional%20atmosphere%2C%20modern%20broadcast%20equipment%2C%20Madrid%20skyline%20view%20through%20window%2C%20editorial%20photography&width=1600&height=600&seq=rc-radio-img-09&orientation=landscape',
    categoria: 'cultural',
    idioma: 'es',
    distancia: 1.1,
    oyentes: 5230,
    locutorPrincipal: 'Valentina Suárez',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Elegant%20Spanish%20female%20radio%20host%20portrait%2C%20sophisticated%20style%2C%20warm%20smile%2C%20professional%20headshot%20in%20studio%2C%20soft%20natural%20lighting&width=80&height=80&seq=rc-loc-avatar-09&orientation=squarish',
    estaEnVivo: true,
    enTendencia: false,
    rating: 4.7,
    tags: ['cultura', 'Madrid', 'entrevistas'],
    descripcion: 'La voz cultural de Madrid. Entrevistas con personalidades, cobertura de exposiciones, teatro y la agenda cultural completa.',
  },
  {
    id: 'rc-radio-10',
    nombre: 'Radio Fiesta Madrid',
    frecuencia: '103.8 RFW',
    ciudad: 'Madrid',
    estado: 'Comunidad de Madrid',
    pais: 'España',
    paisCodigo: 'ES',
    logoUrl: 'https://readdy.ai/api/search-image?query=Festive%20Spanish%20radio%20station%20logo%20with%20confetti%20and%20party%20design%2C%20bright%20yellow%20and%20orange%20colors%2C%20cheerful%20broadcast%20emblem%2C%20vector%20flat%20design&width=200&height=200&seq=rc-radio-logo-10&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Bright%20cheerful%20Spanish%20radio%20studio%20with%20colorful%20decor%2C%20party%20lights%2C%20energetic%20atmosphere%2C%20modern%20broadcast%20setup%2C%20warm%20daylight%2C%20festive%20vibe&width=1600&height=600&seq=rc-radio-img-10&orientation=landscape',
    categoria: 'pop',
    idioma: 'es',
    distancia: 2.4,
    oyentes: 3890,
    locutorPrincipal: 'Pablo Romero',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Energetic%20Spanish%20male%20radio%20DJ%20portrait%2C%20cheerful%20expression%2C%20modern%20casual%20style%2C%20headphones%2C%20fun%20personality%2C%20studio%20lighting&width=80&height=80&seq=rc-loc-avatar-10&orientation=squarish',
    estaEnVivo: true,
    enTendencia: false,
    rating: 4.4,
    tags: ['pop', 'fiesta', 'éxitos'],
    descripcion: 'La radio que prende la fiesta en Madrid. Éxitos del momento, música latina y los mejores DJ sets para tu día.',
  },

  // ── Colombia / Bogotá ──
  {
    id: 'rc-radio-11',
    nombre: 'La Emisora Capitalina',
    frecuencia: '92.4 RFW',
    ciudad: 'Bogotá',
    estado: 'Bogotá D.C.',
    pais: 'Colombia',
    paisCodigo: 'CO',
    logoUrl: 'https://readdy.ai/api/search-image?query=Colombian%20radio%20station%20logo%20with%20Monserrate%20mountain%20silhouette%2C%20yellow%20blue%20red%20flag%20colors%2C%20warm%20design%2C%20broadcast%20emblem%2C%20vector%20flat&width=200&height=200&seq=rc-radio-logo-11&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Warm%20Colombian%20radio%20studio%20in%20Bogota%20with%20mountain%20view%20through%20windows%2C%20Andean%20decor%20accents%2C%20modern%20broadcast%20equipment%2C%20soft%20natural%20daylight%2C%20editorial%20photography&width=1600&height=600&seq=rc-radio-img-11&orientation=landscape',
    categoria: 'cultural',
    idioma: 'es',
    distancia: 1.5,
    oyentes: 4560,
    locutorPrincipal: 'Carolina Ramírez',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Warm%20friendly%20Colombian%20female%20radio%20host%20portrait%2C%20genuine%20smile%2C%20colorful%20professional%20attire%2C%20approachable%20personality%2C%20soft%20studio%20lighting&width=80&height=80&seq=rc-loc-avatar-11&orientation=squarish',
    estaEnVivo: true,
    enTendencia: true,
    rating: 4.8,
    tags: ['cultura', 'Bogotá', 'música colombiana'],
    descripcion: 'El sonido de la capital colombiana. Cultura, noticias de Bogotá y la mejor música andina y tropical.',
  },
  {
    id: 'rc-radio-12',
    nombre: 'Salsa Bogotá FM',
    frecuencia: '99.1 RFW',
    ciudad: 'Bogotá',
    estado: 'Bogotá D.C.',
    pais: 'Colombia',
    paisCodigo: 'CO',
    logoUrl: 'https://readdy.ai/api/search-image?query=Salsa%20radio%20station%20logo%20with%20dancing%20couple%20silhouette%2C%20vibrant%20red%20and%20orange%20colors%2C%20rhythmic%20design%2C%20Latin%20music%20broadcast%20emblem%2C%20vector%20flat&width=200&height=200&seq=rc-radio-logo-12&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Vibrant%20Colombian%20salsa%20radio%20studio%20with%20warm%20red%20and%20orange%20lighting%2C%20dance%20posters%2C%20Latin%20music%20atmosphere%2C%20energetic%20broadcast%20space%2C%20editorial%20photography&width=1600&height=600&seq=rc-radio-img-12&orientation=landscape',
    categoria: 'regional',
    idioma: 'es',
    distancia: 2.8,
    oyentes: 6230,
    locutorPrincipal: 'Miguel Ángel Torres',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20Colombian%20male%20salsa%20radio%20DJ%20portrait%2C%20stylish%20urban%20look%2C%20confident%20smile%2C%20passionate%20music%20personality%2C%20studio%20lighting&width=80&height=80&seq=rc-loc-avatar-12&orientation=squarish',
    estaEnVivo: true,
    enTendencia: false,
    rating: 4.7,
    tags: ['salsa', 'tropical', 'baile'],
    descripcion: 'El ritmo que mueve Bogotá. Salsa, son, guaguancó y toda la música tropical que te hace bailar.',
  },
  {
    id: 'rc-radio-13',
    nombre: 'Rock Extremo Bogotá',
    frecuencia: '105.3 RFW',
    ciudad: 'Bogotá',
    estado: 'Bogotá D.C.',
    pais: 'Colombia',
    paisCodigo: 'CO',
    logoUrl: 'https://readdy.ai/api/search-image?query=Extreme%20rock%20radio%20logo%20with%20skull%20and%20guitars%2C%20dark%20red%20and%20black%20colors%2C%20metal%20music%20broadcast%20emblem%2C%20bold%20aggressive%20design%2C%20vector%20flat&width=200&height=200&seq=rc-radio-logo-13&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Dark%20moody%20rock%20radio%20studio%20in%20Bogota%20with%20band%20posters%2C%20electric%20guitars%20on%20walls%2C%20dim%20amber%20and%20red%20accent%20lighting%2C%20underground%20music%20atmosphere&width=1600&height=600&seq=rc-radio-img-13&orientation=landscape',
    categoria: 'rock',
    idioma: 'es',
    distancia: 3.5,
    oyentes: 2780,
    locutorPrincipal: 'Felipe Rojas',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Colombian%20rock%20radio%20DJ%20portrait%2C%20edgy%20style%2C%20tattoos%2C%20dark%20t-shirt%2C%20cool%20underground%20personality%2C%20moody%20studio%20lighting&width=80&height=80&seq=rc-loc-avatar-13&orientation=squarish',
    estaEnVivo: false,
    enTendencia: false,
    rating: 4.3,
    tags: ['rock', 'metal', 'underground'],
    descripcion: 'El lado más pesado de Bogotá. Rock, metal, punk y la escena under colombiana. Sin concesiones.',
  },

  // ── Costa Rica / San José ──
  {
    id: 'rc-radio-14',
    nombre: 'Radio Pura Vida',
    frecuencia: '91.7 RFW',
    ciudad: 'San José',
    estado: 'San José',
    pais: 'Costa Rica',
    paisCodigo: 'CR',
    logoUrl: 'https://readdy.ai/api/search-image?query=Costa%20Rican%20radio%20station%20logo%20with%20tropical%20flower%20and%20sun%2C%20green%20and%20warm%20yellow%20colors%2C%20friendly%20broadcast%20emblem%2C%20circular%20design%2C%20vector%20flat&width=200&height=200&seq=rc-radio-logo-14&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Bright%20tropical%20Costa%20Rican%20radio%20studio%20with%20plants%20and%20natural%20light%2C%20open%20windows%20with%20green%20mountain%20view%2C%20wooden%20accents%2C%20warm%20relaxed%20atmosphere%2C%20editorial%20photography&width=1600&height=600&seq=rc-radio-img-14&orientation=landscape',
    categoria: 'pop',
    idioma: 'es',
    distancia: 0.5,
    oyentes: 3120,
    locutorPrincipal: 'María José Vargas',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Friendly%20Costa%20Rican%20female%20radio%20host%20portrait%2C%20warm%20smile%2C%20tropical%20casual%20style%2C%20relaxed%20genuine%20expression%2C%20natural%20lighting&width=80&height=80&seq=rc-loc-avatar-14&orientation=squarish',
    estaEnVivo: true,
    enTendencia: true,
    rating: 4.9,
    tags: ['pop', 'tropical', 'Costa Rica'],
    descripcion: 'El sonido de la Pura Vida. Música para sentirte bien, noticias positivas y la mejor energía costarricense.',
  },
  {
    id: 'rc-radio-15',
    nombre: 'Noticias CR Hoy',
    frecuencia: '87.5 RFW',
    ciudad: 'San José',
    estado: 'San José',
    pais: 'Costa Rica',
    paisCodigo: 'CR',
    logoUrl: 'https://readdy.ai/api/search-image?query=Costa%20Rican%20news%20radio%20logo%20with%20globe%20and%20microphone%2C%20blue%20and%20white%20colors%2C%20professional%20broadcast%20emblem%2C%20vector%20flat%20design&width=200&height=200&seq=rc-radio-logo-15&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Professional%20news%20radio%20studio%20in%20San%20Jose%20Costa%20Rica%2C%20modern%20broadcast%20desk%2C%20multiple%20monitors%2C%20clean%20organized%20atmosphere%2C%20tropical%20light%20through%20windows&width=1600&height=600&seq=rc-radio-img-15&orientation=landscape',
    categoria: 'noticias',
    idioma: 'es',
    distancia: 1.3,
    oyentes: 5670,
    locutorPrincipal: 'Luis Fernando Gómez',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Mature%20Costa%20Rican%20news%20anchor%20portrait%2C%20professional%20suit%2C%20distinguished%20look%2C%20trustworthy%20expression%2C%20studio%20lighting&width=80&height=80&seq=rc-loc-avatar-15&orientation=squarish',
    estaEnVivo: true,
    enTendencia: false,
    rating: 4.8,
    tags: ['noticias', 'actualidad', 'Costa Rica'],
    descripcion: 'La información que importa en Costa Rica. Noticias nacionales, política, economía y todo lo que pasa en San José.',
  },
  {
    id: 'rc-radio-16',
    nombre: 'Radio Comunitaria CR',
    frecuencia: '160.00 RFW',
    ciudad: 'San José',
    estado: 'San José',
    pais: 'Costa Rica',
    paisCodigo: 'CR',
    logoUrl: 'https://readdy.ai/api/search-image?query=Community%20radio%20station%20logo%20with%20people%20holding%20hands%2C%20warm%20earth%20tones%2C%20grassroots%20broadcast%20emblem%2C%20circular%20inclusive%20design%2C%20vector%20flat&width=200&height=200&seq=rc-radio-logo-16&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Warm%20community%20radio%20studio%20in%20San%20Jose%20Costa%20Rica%2C%20diverse%20volunteers%2C%20local%20art%20on%20walls%2C%20intimate%20broadcast%20space%2C%20grassroots%20authentic%20atmosphere&width=1600&height=600&seq=rc-radio-img-16&orientation=landscape',
    categoria: 'comunitaria',
    idioma: 'es',
    distancia: 2.2,
    oyentes: 1890,
    locutorPrincipal: 'Ana Lucía Hernández',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Warm%20Costa%20Rican%20community%20radio%20volunteer%20portrait%2C%20genuine%20kind%20smile%2C%20casual%20style%2C%20approachable%20personality%2C%20natural%20outdoor%20lighting&width=80&height=80&seq=rc-loc-avatar-16&orientation=squarish',
    estaEnVivo: false,
    enTendencia: false,
    rating: 4.5,
    tags: ['comunitaria', 'local', 'participativa'],
    descripcion: 'La voz de la comunidad costarricense. Cultura, eventos locales y participación ciudadana desde cada rincón de San José.',
  },

  // ── Más radios CABA (Argentina) ──
  {
    id: 'rc-radio-17',
    nombre: 'Tango y Folklore BA',
    frecuencia: '86.9 RFW',
    ciudad: 'CABA',
    estado: 'Buenos Aires',
    pais: 'Argentina',
    paisCodigo: 'AR',
    logoUrl: 'https://readdy.ai/api/search-image?query=Argentine%20tango%20radio%20logo%20with%20bandoneon%20and%20dancers%2C%20warm%20burgundy%20and%20gold%20colors%2C%20classic%20elegant%20design%2C%20music%20broadcast%20emblem%2C%20vector%20flat&width=200&height=200&seq=rc-radio-logo-17&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Classic%20elegant%20Argentine%20radio%20studio%20with%20bandoneon%20display%2C%20warm%20burgundy%20walls%2C%20vintage%20microphone%2C%20tango%20photographs%2C%20intimate%20broadcast%20atmosphere&width=1600&height=600&seq=rc-radio-img-17&orientation=landscape',
    categoria: 'cultural',
    idioma: 'es',
    distancia: 1.4,
    oyentes: 2340,
    locutorPrincipal: 'Héctor Suárez',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Classic%20Argentine%20tango%20radio%20host%20portrait%2C%20older%20distinguished%20gentleman%2C%20beret%2C%20warm%20wise%20expression%2C%20soft%20studio%20lighting&width=80&height=80&seq=rc-loc-avatar-17&orientation=squarish',
    estaEnVivo: false,
    enTendencia: false,
    rating: 4.6,
    tags: ['tango', 'folklore', 'tradición'],
    descripcion: 'El alma musical de Buenos Aires. Tango, milonga y folklore argentino las 24 horas. La tradición nunca sonó tan bien.',
  },
  {
    id: 'rc-radio-18',
    nombre: 'Electrónica Buenos Aires',
    frecuencia: '104.2 RFW',
    ciudad: 'CABA',
    estado: 'Buenos Aires',
    pais: 'Argentina',
    paisCodigo: 'AR',
    logoUrl: 'https://readdy.ai/api/search-image?query=Electronic%20music%20radio%20logo%20with%20neon%20purple%20and%20cyan%2C%20sound%20wave%20and%20city%20skyline%2C%20modern%20DJ%20broadcast%20emblem%2C%20vector%20flat%20design&width=200&height=200&seq=rc-radio-logo-18&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Sleek%20modern%20electronic%20music%20radio%20studio%20in%20Buenos%20Aires%2C%20purple%20and%20cyan%20neon%20accent%20lighting%2C%20DJ%20equipment%2C%20LED%20panels%2C%20night%20club%20atmosphere&width=1600&height=600&seq=rc-radio-img-18&orientation=landscape',
    categoria: 'electronica',
    idioma: 'es',
    distancia: 4.7,
    oyentes: 1980,
    locutorPrincipal: 'Luciana Paz',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Argentine%20female%20DJ%20portrait%2C%20futuristic%20style%2C%20colorful%20hair%2C%20headphones%2C%20cool%20confident%20expression%2C%20neon%20studio%20lighting&width=80&height=80&seq=rc-loc-avatar-18&orientation=squarish',
    estaEnVivo: false,
    enTendencia: false,
    rating: 4.2,
    tags: ['electrónica', 'techno', 'house'],
    descripcion: 'El beat de Buenos Aires. Techno, house, progressive y los DJs que mueven la noche porteña.',
  },

  // ── Más radios CDMX ──
  {
    id: 'rc-radio-19',
    nombre: 'Deportiva MX Radio',
    frecuencia: '90.8 RFW',
    ciudad: 'CDMX',
    estado: 'Ciudad de México',
    pais: 'México',
    paisCodigo: 'MX',
    logoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20sports%20radio%20logo%20with%20soccer%20ball%20and%20Aztec%20stadium%2C%20green%20red%20white%20colors%2C%20dynamic%20broadcast%20emblem%2C%20vector%20flat&width=200&height=200&seq=rc-radio-logo-19&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Energetic%20Mexican%20sports%20radio%20studio%20with%20soccer%20jerseys%20on%20wall%2C%20trophy%20display%2C%20multiple%20TV%20screens%20showing%20live%20sports%2C%20passionate%20atmosphere&width=1600&height=600&seq=rc-radio-img-19&orientation=landscape',
    categoria: 'deportiva',
    idioma: 'es',
    distancia: 2.9,
    oyentes: 7450,
    locutorPrincipal: 'Omar Castillo',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Passionate%20Mexican%20sports%20commentator%20portrait%2C%20energetic%20expression%2C%20headset%20mic%2C%20team%20jersey%2C%20professional%20headshot&width=80&height=80&seq=rc-loc-avatar-19&orientation=squarish',
    estaEnVivo: true,
    enTendencia: false,
    rating: 4.7,
    tags: ['deportes', 'fútbol mexicano', 'liga MX'],
    descripcion: 'La pasión del deporte mexicano. Liga MX, NFL, boxeo y todo el deporte que hace vibrar a México.',
  },

  // ── Más radios Madrid ──
  {
    id: 'rc-radio-20',
    nombre: 'Radio Deportes Madrid',
    frecuencia: '100.6 RFW',
    ciudad: 'Madrid',
    estado: 'Comunidad de Madrid',
    pais: 'España',
    paisCodigo: 'ES',
    logoUrl: 'https://readdy.ai/api/search-image?query=Spanish%20sports%20radio%20logo%20with%20Real%20Madrid%20crown%20motif%2C%20white%20and%20gold%20colors%2C%20elegant%20broadcast%20emblem%2C%20vector%20flat%20design&width=200&height=200&seq=rc-radio-logo-20&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Elegant%20Spanish%20sports%20radio%20studio%20in%20Madrid%20with%20football%20memorabilia%2C%20Real%20Madrid%20trophy%20photos%2C%20professional%20broadcast%20atmosphere%2C%20editorial%20photography&width=1600&height=600&seq=rc-radio-img-20&orientation=landscape',
    categoria: 'deportiva',
    idioma: 'es',
    distancia: 3.6,
    oyentes: 6780,
    locutorPrincipal: 'Alberto Fernández',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Professional%20Spanish%20sports%20journalist%20portrait%2C%20confident%20expression%2C%20smart%20casual%20attire%2C%20experienced%20broadcaster%2C%20studio%20lighting&width=80&height=80&seq=rc-loc-avatar-20&orientation=squarish',
    estaEnVivo: true,
    enTendencia: false,
    rating: 4.8,
    tags: ['deportes', 'fútbol español', 'La Liga'],
    descripcion: 'El deporte madrileño en estado puro. La Liga, Champions, baloncesto y toda la actualidad del deporte español.',
  },

  // ── Bogotá extra ──
  {
    id: 'rc-radio-21',
    nombre: 'Radio Comunitaria Bogotá',
    frecuencia: '131.50 RFW',
    ciudad: 'Bogotá',
    estado: 'Bogotá D.C.',
    pais: 'Colombia',
    paisCodigo: 'CO',
    logoUrl: 'https://readdy.ai/api/search-image?query=Colombian%20community%20radio%20logo%20with%20diverse%20people%20and%20mountains%2C%20green%20and%20warm%20orange%20colors%2C%20grassroots%20broadcast%20emblem%2C%20vector%20flat&width=200&height=200&seq=rc-radio-logo-21&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Warm%20community%20radio%20studio%20in%20Bogota%20neighborhood%2C%20local%20art%20on%20walls%2C%20diverse%20team%20around%20microphones%2C%20intimate%20authentic%20space%2C%20daylight%20streaming&width=1600&height=600&seq=rc-radio-img-21&orientation=landscape',
    categoria: 'comunitaria',
    idioma: 'es',
    distancia: 2.1,
    oyentes: 1560,
    locutorPrincipal: 'Diana Marcela Ruiz',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Warm%20Colombian%20female%20community%20radio%20host%20portrait%2C%20kind%20smile%2C%20colorful%20indigenous%20style%20scarf%2C%20genuine%20personality%2C%20natural%20lighting&width=80&height=80&seq=rc-loc-avatar-21&orientation=squarish',
    estaEnVivo: false,
    enTendencia: false,
    rating: 4.4,
    tags: ['comunitaria', 'local', 'diversidad'],
    descripcion: 'Las voces de los barrios bogotanos. Programación hecha por y para la comunidad. Diversidad y participación.',
  },

  // ── San José CR extra ──
  {
    id: 'rc-radio-22',
    nombre: 'Rock Tico FM',
    frecuencia: '106.1 RFW',
    ciudad: 'San José',
    estado: 'San José',
    pais: 'Costa Rica',
    paisCodigo: 'CR',
    logoUrl: 'https://readdy.ai/api/search-image?query=Costa%20Rican%20rock%20radio%20logo%20with%20volcano%20and%20guitar%2C%20green%20and%20black%20colors%2C%20bold%20music%20broadcast%20emblem%2C%20vector%20flat%20design&width=200&height=200&seq=rc-radio-logo-22&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Costa%20Rican%20rock%20radio%20studio%20with%20band%20posters%20and%20local%20art%2C%20warm%20lighting%2C%20guitar%20collection%2C%20tropical%20view%20through%20window%2C%20authentic%20music%20space&width=1600&height=600&seq=rc-radio-img-22&orientation=landscape',
    categoria: 'rock',
    idioma: 'es',
    distancia: 3.8,
    oyentes: 1230,
    locutorPrincipal: 'Andrés Castillo',
    locutorAvatarUrl: 'https://readdy.ai/api/search-image?query=Costa%20Rican%20rock%20musician%20portrait%2C%20guitar%2C%20band%20t-shirt%2C%20cool%20relaxed%20vibe%2C%20tropical%20lighting%2C%20authentic%20personality&width=80&height=80&seq=rc-loc-avatar-22&orientation=squarish',
    estaEnVivo: false,
    enTendencia: false,
    rating: 4.3,
    tags: ['rock', 'indie', 'tico'],
    descripcion: 'El rock hecho en Costa Rica. Bandas ticas, indie latinoamericano y los clásicos del rock internacional.',
  },
];

// ─── Eventos Cercanos ───

export const eventosCercanos: EventoCercano[] = [
  {
    id: 'rc-evento-01',
    titulo: 'Festival Sonidos del Centro — CDMX',
    tipo: 'festival',
    ciudad: 'CDMX',
    estado: 'Ciudad de México',
    pais: 'México',
    paisCodigo: 'MX',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Large%20outdoor%20music%20festival%20in%20Mexico%20City%20main%20square%2C%20colorful%20stage%20with%20lights%2C%20big%20crowd%2C%20historic%20buildings%20background%2C%20warm%20evening%20atmosphere%2C%20vibrant%20cultural%20celebration&width=800&height=500&seq=rc-evento-img-01&orientation=landscape',
    radioNombre: 'Estéreo Capital',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Modern%20urban%20radio%20station%20logo%20with%20Mexico%20City%20skyline%2C%20mustard%20yellow%20and%20charcoal%2C%20bold%20graphic%20design&width=80&height=80&seq=rc-evento-logo-01&orientation=squarish',
    fecha: '2026-07-15',
    hora: '16:00',
    distancia: 2.5,
    participantes: 3200,
    esGratuito: true,
    descripcion: 'El festival gratuito más grande del centro de CDMX. 12 bandas en vivo, food trucks, mercado de artesanías y transmisión especial en vivo.',
    tags: ['festival', 'gratis', 'música en vivo'],
  },
  {
    id: 'rc-evento-02',
    titulo: 'Noche de Rock en el Zócalo',
    tipo: 'concierto',
    ciudad: 'CDMX',
    estado: 'Ciudad de México',
    pais: 'México',
    paisCodigo: 'MX',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Rock%20concert%20at%20Zocalo%20Mexico%20City%20at%20night%2C%20electric%20guitar%20players%20on%20stage%2C%20dramatic%20stage%20lighting%2C%20enthusiastic%20crowd%2C%20historic%20cathedral%20in%20background&width=800&height=500&seq=rc-evento-img-02&orientation=landscape',
    radioNombre: 'Rock Total CDMX',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20with%20guitar%20and%20lightning%20bolt%2C%20black%20and%20red%2C%20bold%20edgy%20design&width=80&height=80&seq=rc-evento-logo-02&orientation=squarish',
    fecha: '2026-07-22',
    hora: '20:00',
    distancia: 0.8,
    participantes: 8900,
    esGratuito: true,
    descripcion: 'Noche épica de rock en el Zócalo capitalino. Bandas locales y headliners internacionales. Entrada libre para toda la familia.',
    tags: ['rock', 'concierto', 'gratis'],
  },
  {
    id: 'rc-evento-03',
    titulo: 'Buenos Aires Jazz Nights',
    tipo: 'festival',
    ciudad: 'CABA',
    estado: 'Buenos Aires',
    pais: 'Argentina',
    paisCodigo: 'AR',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Elegant%20jazz%20festival%20in%20Buenos%20Aires%20theater%2C%20warm%20intimate%20lighting%2C%20saxophone%20player%20on%20stage%2C%20sophisticated%20audience%2C%20vintage%20modern%20atmosphere&width=800&height=500&seq=rc-evento-img-03&orientation=landscape',
    radioNombre: 'FM Sol 94.70',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Argentine%20radio%20station%20logo%20with%20sun%20motif%2C%20warm%20orange%20and%20yellow%2C%20friendly%20broadcast%20emblem&width=80&height=80&seq=rc-evento-logo-03&orientation=squarish',
    fecha: '2026-08-05',
    hora: '19:30',
    distancia: 1.2,
    participantes: 1500,
    esGratuito: false,
    descripcion: 'Tres noches del mejor jazz en el Teatro Colón. Artistas internacionales y los mejores jazzistas argentinos.',
    tags: ['jazz', 'festival', 'teatro'],
  },
  {
    id: 'rc-evento-04',
    titulo: 'Encuentro de Radios Comunitarias — CABA',
    tipo: 'encuentro',
    ciudad: 'CABA',
    estado: 'Buenos Aires',
    pais: 'Argentina',
    paisCodigo: 'AR',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Community%20radio%20gathering%20in%20Buenos%20Aires%20cultural%20center%2C%20diverse%20group%20of%20broadcasters%20networking%2C%20workshop%20atmosphere%2C%20colorful%20banners%2C%20warm%20indoor%20lighting&width=800&height=500&seq=rc-evento-img-04&orientation=landscape',
    radioNombre: 'Radio Comunitaria CR',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Community%20radio%20station%20logo%20with%20people%20holding%20hands%2C%20warm%20earth%20tones%2C%20grassroots%20broadcast%20emblem&width=80&height=80&seq=rc-evento-logo-04&orientation=squarish',
    fecha: '2026-07-28',
    hora: '10:00',
    distancia: 3.1,
    participantes: 450,
    esGratuito: true,
    descripcion: 'Encuentro anual de radios comunitarias argentinas. Workshops, paneles de discusión y networking entre radialistas.',
    tags: ['comunitario', 'networking', 'gratis'],
  },
  {
    id: 'rc-evento-05',
    titulo: 'Madrid Suena — Festival de Radio',
    tipo: 'festival',
    ciudad: 'Madrid',
    estado: 'Comunidad de Madrid',
    pais: 'España',
    paisCodigo: 'ES',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Large%20radio%20festival%20in%20Madrid%20park%2C%20outdoor%20stages%20with%20radio%20brands%2C%20crowds%20enjoying%20music%2C%20sunny%20summer%20day%2C%20Spanish%20flags%2C%20vibrant%20atmosphere&width=800&height=500&seq=rc-evento-img-05&orientation=landscape',
    radioNombre: 'Onda Capital Madrid',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Spanish%20radio%20station%20logo%20with%20bear%20and%20strawberry%20tree%20Madrid%20motif%2C%20red%20and%20cream%2C%20elegant%20design&width=80&height=80&seq=rc-evento-logo-05&orientation=squarish',
    fecha: '2026-09-10',
    hora: '12:00',
    distancia: 2.7,
    participantes: 15000,
    esGratuito: true,
    descripcion: 'El mayor festival de radio de España. 20 emisoras, 50 artistas, food trucks y actividades para toda la familia en el Parque del Retiro.',
    tags: ['festival', 'radio', 'gratis'],
  },
  {
    id: 'rc-evento-06',
    titulo: 'Noche de Tango en San Telmo',
    tipo: 'cultural',
    ciudad: 'CABA',
    estado: 'Buenos Aires',
    pais: 'Argentina',
    paisCodigo: 'AR',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Tango%20night%20in%20San%20Telmo%20Buenos%20Aires%2C%20couples%20dancing%20under%20string%20lights%2C%20live%20orchestra%2C%20cobblestone%20street%2C%20warm%20romantic%20evening%20atmosphere&width=800&height=500&seq=rc-evento-img-06&orientation=landscape',
    radioNombre: 'Tango y Folklore BA',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Argentine%20tango%20radio%20logo%20with%20bandoneon%20and%20dancers%2C%20warm%20burgundy%20and%20gold&width=80&height=80&seq=rc-evento-logo-06&orientation=squarish',
    fecha: '2026-07-08',
    hora: '21:00',
    distancia: 0.9,
    participantes: 680,
    esGratuito: false,
    descripcion: 'Milonga al aire libre en las calles de San Telmo. Orquesta en vivo, clases de tango y la mejor gastronomía porteña.',
    tags: ['tango', 'milonga', 'cultural'],
  },
  {
    id: 'rc-evento-07',
    titulo: 'Carnaval de Barranquilla en Bogotá',
    tipo: 'festival',
    ciudad: 'Bogotá',
    estado: 'Bogotá D.C.',
    pais: 'Colombia',
    paisCodigo: 'CO',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Colorful%20Colombian%20carnival%20celebration%20in%20Bogota%2C%20dancers%20in%20vibrant%20costumes%2C%20parade%20on%20main%20street%2C%20crowds%20cheering%2C%20sunny%20mountain%20backdrop&width=800&height=500&seq=rc-evento-img-07&orientation=landscape',
    radioNombre: 'La Emisora Capitalina',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Colombian%20radio%20station%20logo%20with%20Monserrate%20mountain%20silhouette%2C%20yellow%20blue%20red&width=80&height=80&seq=rc-evento-logo-07&orientation=squarish',
    fecha: '2026-08-12',
    hora: '14:00',
    distancia: 3.5,
    participantes: 12000,
    esGratuito: true,
    descripcion: 'El carnaval más grande de Colombia llega a Bogotá. Comparsas, música en vivo, gastronomía y la mejor fiesta cultural.',
    tags: ['carnaval', 'cultura', 'gratis'],
  },
  {
    id: 'rc-evento-08',
    titulo: 'Feria de Música Independiente — Madrid',
    tipo: 'feria',
    ciudad: 'Madrid',
    estado: 'Comunidad de Madrid',
    pais: 'España',
    paisCodigo: 'ES',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Independent%20music%20fair%20in%20Madrid%20cultural%20center%2C%20vinyl%20record%20stalls%2C%20indie%20artists%20showcasing%2C%20young%20creative%20crowd%2C%20industrial%20chic%20venue%2C%20warm%20lighting&width=800&height=500&seq=rc-evento-img-08&orientation=landscape',
    radioNombre: 'Radio Fiesta Madrid',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Festive%20Spanish%20radio%20station%20logo%20with%20confetti%20and%20party%20design%2C%20bright%20yellow%20and%20orange&width=80&height=80&seq=rc-evento-logo-08&orientation=squarish',
    fecha: '2026-09-23',
    hora: '11:00',
    distancia: 4.2,
    participantes: 2300,
    esGratuito: true,
    descripcion: 'Feria de sellos independientes, vinilos, conciertos acústicos y networking para músicos emergentes en Madrid.',
    tags: ['indie', 'feria', 'música'],
  },
  {
    id: 'rc-evento-09',
    titulo: 'Festival Tropical San José',
    tipo: 'festival',
    ciudad: 'San José',
    estado: 'San José',
    pais: 'Costa Rica',
    paisCodigo: 'CR',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Tropical%20music%20festival%20in%20San%20Jose%20Costa%20Rica%2C%20beach%20vibe%20stage%2C%20palm%20trees%2C%20colorful%20decorations%2C%20happy%20dancing%20crowd%2C%20sunny%20warm%20day&width=800&height=500&seq=rc-evento-img-09&orientation=landscape',
    radioNombre: 'Radio Pura Vida',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Costa%20Rican%20radio%20station%20logo%20with%20tropical%20flower%20and%20sun%2C%20green%20and%20warm%20yellow&width=80&height=80&seq=rc-evento-logo-09&orientation=squarish',
    fecha: '2026-07-30',
    hora: '15:00',
    distancia: 1.7,
    participantes: 5400,
    esGratuito: true,
    descripcion: 'El festival tropical más grande de Costa Rica. Música, playa, food trucks y la mejor vibra caribeña en pleno San José.',
    tags: ['tropical', 'festival', 'gratis'],
  },
  {
    id: 'rc-evento-10',
    titulo: 'Debate: El Futuro de la Radio — CDMX',
    tipo: 'encuentro',
    ciudad: 'CDMX',
    estado: 'Ciudad de México',
    pais: 'México',
    paisCodigo: 'MX',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Professional%20panel%20discussion%20in%20Mexico%20City%20conference%20room%2C%20radio%20broadcasters%20on%20stage%2C%20modern%20auditorium%2C%20engaged%20audience%2C%20broadcast%20equipment&width=800&height=500&seq=rc-evento-img-10&orientation=landscape',
    radioNombre: 'Radio Noticias 24/7',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=News%20radio%20station%20logo%20with%20bold%20typography%2C%20red%20and%20white%2C%20globe%20motif&width=80&height=80&seq=rc-evento-logo-10&orientation=squarish',
    fecha: '2026-08-18',
    hora: '09:00',
    distancia: 4.5,
    participantes: 320,
    esGratuito: true,
    descripcion: 'Panel de expertos discutiendo el futuro de la radio digital. Streaming, podcasts, IA y nuevas plataformas.',
    tags: ['debate', 'radio', 'tecnología'],
  },
  {
    id: 'rc-evento-11',
    titulo: 'Feria de la Empanada — CABA',
    tipo: 'festival',
    ciudad: 'CABA',
    estado: 'Buenos Aires',
    pais: 'Argentina',
    paisCodigo: 'AR',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Argentine%20empanada%20food%20festival%20in%20Buenos%20Aires%20park%2C%20food%20stalls%20with%20variety%20of%20empanadas%2C%20families%20enjoying%2C%20live%20folk%20music%2C%20sunny%20winter%20day&width=800&height=500&seq=rc-evento-img-11&orientation=landscape',
    radioNombre: 'Radio Deportiva Sur',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20soccer%20ball%20and%20whistle%2C%20green%20and%20white&width=80&height=80&seq=rc-evento-logo-11&orientation=squarish',
    fecha: '2026-08-02',
    hora: '12:00',
    distancia: 2.8,
    participantes: 7800,
    esGratuito: false,
    descripcion: 'La feria gastronómica más esperada de Buenos Aires. 50 puestos de empanadas, música en vivo y concurso de la mejor empanada.',
    tags: ['gastronomía', 'festival', 'familia'],
  },
];

// ─── Locutores Cercanos ───

export const locutoresCercanos: LocutorCercano[] = [
  {
    id: 'rc-locutor-01',
    nombre: 'Adriana Mejía',
    ciudad: 'CDMX',
    estado: 'Ciudad de México',
    pais: 'México',
    paisCodigo: 'MX',
    avatarUrl: 'https://readdy.ai/api/search-image?query=Warm%20friendly%20Mexican%20female%20radio%20host%20portrait%2C%20genuine%20smile%2C%20colorful%20blouse%2C%20professional%20headshot%20in%20studio%2C%20soft%20natural%20lighting&width=160&height=160&seq=rc-loc-img-01&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Mexican%20female%20radio%20host%20broadcasting%20live%20in%20colorful%20studio%2C%20headphones%2C%20microphone%2C%20warm%20professional%20atmosphere%2C%20editorial%20photography&width=800&height=500&seq=rc-loc-cover-01&orientation=landscape',
    radioNombre: 'La Mexicana FM',
    radioFrecuencia: '98.5 RFW',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20radio%20station%20logo%20with%20serape%20colors%20red%20green%20white%2C%20circular%20design&width=80&height=80&seq=rc-loc-radio-01&orientation=squarish',
    distancia: 1.2,
    seguidores: 45200,
    estaEnVivo: true,
    especialidad: 'Regional Mexicano',
    idioma: 'es',
    bio: 'La dama del regional mexicano. 15 años al aire conectando generaciones con la música de raíz. Su programa matutino es el más escuchado del género en CDMX.',
    tags: ['regional', 'cultura', 'tradición'],
  },
  {
    id: 'rc-locutor-02',
    nombre: 'Carlos Mendoza',
    ciudad: 'CDMX',
    estado: 'Ciudad de México',
    pais: 'México',
    paisCodigo: 'MX',
    avatarUrl: 'https://readdy.ai/api/search-image?query=Senior%20Mexican%20news%20anchor%20portrait%2C%20distinguished%20silver%20hair%2C%20professional%20suit%2C%20authoritative%20expression%2C%20studio%20lighting&width=160&height=160&seq=rc-loc-img-02&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Senior%20Mexican%20news%20anchor%20in%20professional%20radio%20studio%2C%20reading%20news%2C%20multiple%20monitors%20behind%2C%20serious%20professional%20atmosphere&width=800&height=500&seq=rc-loc-cover-02&orientation=landscape',
    radioNombre: 'Radio Noticias 24/7',
    radioFrecuencia: '88.1 RFW',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=News%20radio%20station%20logo%20with%20bold%20typography%2C%20red%20and%20white%2C%20globe%20motif&width=80&height=80&seq=rc-loc-radio-02&orientation=squarish',
    distancia: 3.8,
    seguidores: 87300,
    estaEnVivo: true,
    especialidad: 'Periodismo',
    idioma: 'es',
    bio: 'Periodista con más de 20 años en la radio mexicana. Ha cubierto elecciones, desastres naturales y eventos históricos. Su voz es sinónimo de credibilidad.',
    tags: ['noticias', 'periodismo', 'credibilidad'],
  },
  {
    id: 'rc-locutor-03',
    nombre: 'Martín Castellanos',
    ciudad: 'CABA',
    estado: 'Buenos Aires',
    pais: 'Argentina',
    paisCodigo: 'AR',
    avatarUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20Argentine%20male%20radio%20host%20portrait%2C%20friendly%20smile%2C%20casual%20professional%20look%2C%20warm%20natural%20lighting&width=160&height=160&seq=rc-loc-img-03&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20Argentine%20radio%20host%20broadcasting%20morning%20show%2C%20coffee%20on%20desk%2C%20Buenos%20Aires%20skyline%20through%20window%2C%20warm%20morning%20light&width=800&height=500&seq=rc-loc-cover-03&orientation=landscape',
    radioNombre: 'FM Sol 94.70',
    radioFrecuencia: '94.70 RFW',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Argentine%20radio%20station%20logo%20with%20sun%20motif%2C%20warm%20orange%20and%20yellow%2C%20friendly%20broadcast%20emblem&width=80&height=80&seq=rc-loc-radio-03&orientation=squarish',
    distancia: 0.8,
    seguidores: 125000,
    estaEnVivo: true,
    especialidad: 'Entretenimiento Matutino',
    idioma: 'es',
    bio: 'El hombre que despierta a Buenos Aires. Su programa matutino es una institución. Entrevistas, humor y la mejor música para arrancar el día.',
    tags: ['matutino', 'entretenimiento', 'entrevistas'],
  },
  {
    id: 'rc-locutor-04',
    nombre: 'Valentina Suárez',
    ciudad: 'Madrid',
    estado: 'Comunidad de Madrid',
    pais: 'España',
    paisCodigo: 'ES',
    avatarUrl: 'https://readdy.ai/api/search-image?query=Elegant%20Spanish%20female%20radio%20host%20portrait%2C%20sophisticated%20style%2C%20warm%20smile%2C%20professional%20headshot%20in%20studio%2C%20soft%20natural%20lighting&width=160&height=160&seq=rc-loc-img-04&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Elegant%20Spanish%20female%20radio%20host%20in%20modern%20Madrid%20studio%2C%20interviewing%20guest%2C%20cultural%20program%2C%20sophisticated%20atmosphere%2C%20warm%20lighting&width=800&height=500&seq=rc-loc-cover-04&orientation=landscape',
    radioNombre: 'Onda Capital Madrid',
    radioFrecuencia: '96.2 RFW',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Spanish%20radio%20station%20logo%20with%20bear%20and%20strawberry%20tree%20Madrid%20motif%2C%20red%20and%20cream&width=80&height=80&seq=rc-loc-radio-04&orientation=squarish',
    distancia: 1.1,
    seguidores: 67800,
    estaEnVivo: true,
    especialidad: 'Cultura y Artes',
    idioma: 'es',
    bio: 'Periodista cultural y locutora. Su programa es referencia de la agenda cultural madrileña. Ha entrevistado a más de 300 personalidades del arte.',
    tags: ['cultura', 'artes', 'entrevistas'],
  },
  {
    id: 'rc-locutor-05',
    nombre: 'Andrés Castillo',
    ciudad: 'CABA',
    estado: 'Buenos Aires',
    pais: 'Argentina',
    paisCodigo: 'AR',
    avatarUrl: 'https://readdy.ai/api/search-image?query=Argentine%20rock%20radio%20DJ%20portrait%2C%20musician%20vibe%2C%20long%20hair%2C%20black%20t-shirt%2C%20guitar%20pick%20necklace%2C%20cool%20authentic%20personality&width=160&height=160&seq=rc-loc-img-05&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Argentine%20rock%20radio%20DJ%20in%20dark%20moody%20studio%20with%20guitar%20collection%2C%20broadcasting%20rock%20show%2C%20vinyl%20records%20on%20shelves%2C%20amber%20lighting&width=800&height=500&seq=rc-loc-cover-05&orientation=landscape',
    radioNombre: 'Rock Nacional BA',
    radioFrecuencia: '101.5 RFW',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Argentine%20rock%20national%20radio%20logo%20with%20guitar%20and%20flag%20colors%2C%20sky%20blue%20and%20white&width=80&height=80&seq=rc-loc-radio-05&orientation=squarish',
    distancia: 1.9,
    seguidores: 54300,
    estaEnVivo: true,
    especialidad: 'Rock Nacional',
    idioma: 'es',
    bio: 'Guitarrista y locutor. Vive y respira rock argentino. Su programa es cita obligada para los amantes de las guitarras. También toca en una banda de rock under.',
    tags: ['rock', 'guitarra', 'música'],
  },
  {
    id: 'rc-locutor-06',
    nombre: 'Carolina Ramírez',
    ciudad: 'Bogotá',
    estado: 'Bogotá D.C.',
    pais: 'Colombia',
    paisCodigo: 'CO',
    avatarUrl: 'https://readdy.ai/api/search-image?query=Warm%20friendly%20Colombian%20female%20radio%20host%20portrait%2C%20genuine%20smile%2C%20colorful%20professional%20attire%2C%20approachable%20personality%2C%20soft%20studio%20lighting&width=160&height=160&seq=rc-loc-img-06&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Colombian%20female%20radio%20host%20in%20warm%20Bogota%20studio%20with%20mountain%20view%2C%20broadcasting%20cultural%20program%2C%20colorful%20Andean%20textiles%20on%20wall&width=800&height=500&seq=rc-loc-cover-06&orientation=landscape',
    radioNombre: 'La Emisora Capitalina',
    radioFrecuencia: '92.4 RFW',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Colombian%20radio%20station%20logo%20with%20Monserrate%20mountain%20silhouette%2C%20yellow%20blue%20red&width=80&height=80&seq=rc-loc-radio-06&orientation=squarish',
    distancia: 1.5,
    seguidores: 38900,
    estaEnVivo: true,
    especialidad: 'Cultura Andina',
    idioma: 'es',
    bio: 'Comunicadora social y activista cultural. Su programa celebra la diversidad de la región andina. Música, historias y la voz de los territorios.',
    tags: ['cultura', 'andino', 'diversidad'],
  },
  {
    id: 'rc-locutor-07',
    nombre: 'Javier Solís',
    ciudad: 'CDMX',
    estado: 'Ciudad de México',
    pais: 'México',
    paisCodigo: 'MX',
    avatarUrl: 'https://readdy.ai/api/search-image?query=Mexican%20rock%20radio%20DJ%20portrait%2C%20long%20hair%2C%20leather%20jacket%2C%20cool%20rock%20vibe%2C%20moody%20studio%20lighting&width=160&height=160&seq=rc-loc-img-07&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Mexican%20rock%20radio%20DJ%20in%20dark%20studio%20with%20guitar%20amps%2C%20broadcasting%20metal%20show%2C%20concert%20posters%20on%20wall%2C%20red%20lighting&width=800&height=500&seq=rc-loc-cover-07&orientation=landscape',
    radioNombre: 'Rock Total CDMX',
    radioFrecuencia: '95.7 RFW',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20with%20guitar%20and%20lightning%20bolt%2C%20black%20and%20red&width=80&height=80&seq=rc-loc-radio-07&orientation=squarish',
    distancia: 4.1,
    seguidores: 32100,
    estaEnVivo: false,
    especialidad: 'Rock y Metal',
    idioma: 'es',
    bio: 'El hombre detrás del rock más pesado de la CDMX. Su programa nocturno es templo del metal, punk y hard rock. Organizador de conciertos under.',
    tags: ['rock', 'metal', 'underground'],
  },
  {
    id: 'rc-locutor-08',
    nombre: 'María José Vargas',
    ciudad: 'San José',
    estado: 'San José',
    pais: 'Costa Rica',
    paisCodigo: 'CR',
    avatarUrl: 'https://readdy.ai/api/search-image?query=Friendly%20Costa%20Rican%20female%20radio%20host%20portrait%2C%20warm%20smile%2C%20tropical%20casual%20style%2C%20relaxed%20genuine%20expression%2C%20natural%20lighting&width=160&height=160&seq=rc-loc-img-08&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Costa%20Rican%20female%20radio%20host%20in%20bright%20tropical%20studio%20with%20plants%2C%20green%20mountain%20view%20through%20window%2C%20upbeat%20morning%20show&width=800&height=500&seq=rc-loc-cover-08&orientation=landscape',
    radioNombre: 'Radio Pura Vida',
    radioFrecuencia: '91.7 RFW',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Costa%20Rican%20radio%20station%20logo%20with%20tropical%20flower%20and%20sun%2C%20green%20and%20warm%20yellow&width=80&height=80&seq=rc-loc-radio-08&orientation=squarish',
    distancia: 0.5,
    seguidores: 27600,
    estaEnVivo: true,
    especialidad: 'Música Tropical',
    idioma: 'es',
    bio: 'La voz más alegre de Costa Rica. Su programa matutino contagia energía positiva. Música tropical, noticias buenas y mucha Pura Vida.',
    tags: ['tropical', 'positiva', 'matutino'],
  },
  {
    id: 'rc-locutor-09',
    nombre: 'Roberto Díaz',
    ciudad: 'CABA',
    estado: 'Buenos Aires',
    pais: 'Argentina',
    paisCodigo: 'AR',
    avatarUrl: 'https://readdy.ai/api/search-image?query=Energetic%20Argentine%20sports%20commentator%20portrait%2C%20passionate%20expression%2C%20casual%20sporty%20style%2C%20headset%20microphone&width=160&height=160&seq=rc-loc-img-09&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Passionate%20Argentine%20sports%20commentator%20in%20studio%2C%20watching%20live%20match%20on%20multiple%20screens%2C%20football%20memorabilia%2C%20energetic%20atmosphere&width=800&height=500&seq=rc-loc-cover-09&orientation=landscape',
    radioNombre: 'Radio Deportiva Sur',
    radioFrecuencia: '89.3 RFW',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20soccer%20ball%20and%20whistle%2C%20green%20and%20white&width=80&height=80&seq=rc-loc-radio-09&orientation=squarish',
    distancia: 3.2,
    seguidores: 89000,
    estaEnVivo: true,
    especialidad: 'Fútbol y Deportes',
    idioma: 'es',
    bio: 'La voz de los goles argentinos. Comentarista deportivo con más de 20 años de trayectoria. Cubrió 4 mundiales y 6 copas América. Pasión sin filtro.',
    tags: ['fútbol', 'deportes', 'comentarios'],
  },
  {
    id: 'rc-locutor-10',
    nombre: 'Pablo Romero',
    ciudad: 'Madrid',
    estado: 'Comunidad de Madrid',
    pais: 'España',
    paisCodigo: 'ES',
    avatarUrl: 'https://readdy.ai/api/search-image?query=Energetic%20Spanish%20male%20radio%20DJ%20portrait%2C%20cheerful%20expression%2C%20modern%20casual%20style%2C%20headphones%2C%20fun%20personality&width=160&height=160&seq=rc-loc-img-10&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Vibrant%20Spanish%20radio%20DJ%20in%20colorful%20Madrid%20studio%2C%20mixing%20music%20live%2C%20party%20lights%2C%20energetic%20atmosphere%2C%20modern%20broadcast%20space&width=800&height=500&seq=rc-loc-cover-10&orientation=landscape',
    radioNombre: 'Radio Fiesta Madrid',
    radioFrecuencia: '103.8 RFW',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Festive%20Spanish%20radio%20station%20logo%20with%20confetti%20and%20party%20design%2C%20bright%20yellow%20and%20orange&width=80&height=80&seq=rc-loc-radio-10&orientation=squarish',
    distancia: 2.4,
    seguidores: 41200,
    estaEnVivo: true,
    especialidad: 'Música Pop y Fiesta',
    idioma: 'es',
    bio: 'El DJ que prende la fiesta en Madrid. Su programa de tardes es el más divertido de la radio española. Éxitos, concursos y buen rollo garantizado.',
    tags: ['pop', 'fiesta', 'DJ'],
  },
  {
    id: 'rc-locutor-11',
    nombre: 'Miguel Ángel Torres',
    ciudad: 'Bogotá',
    estado: 'Bogotá D.C.',
    pais: 'Colombia',
    paisCodigo: 'CO',
    avatarUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20Colombian%20male%20salsa%20radio%20DJ%20portrait%2C%20stylish%20urban%20look%2C%20confident%20smile%2C%20passionate%20music%20personality&width=160&height=160&seq=rc-loc-img-11&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Colombian%20salsa%20radio%20DJ%20in%20vibrant%20Bogota%20studio%2C%20dancing%20while%20broadcasting%2C%20Latin%20music%20posters%2C%20colorful%20warm%20atmosphere&width=800&height=500&seq=rc-loc-cover-11&orientation=landscape',
    radioNombre: 'Salsa Bogotá FM',
    radioFrecuencia: '99.1 RFW',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Salsa%20radio%20station%20logo%20with%20dancing%20couple%20silhouette%2C%20vibrant%20red%20and%20orange&width=80&height=80&seq=rc-loc-radio-11&orientation=squarish',
    distancia: 2.8,
    seguidores: 47800,
    estaEnVivo: true,
    especialidad: 'Salsa y Música Tropical',
    idioma: 'es',
    bio: 'Bailarín y locutor. La salsa corre por sus venas. Su programa es una rumba diaria que conecta a Colombia con el Caribe. Nadie pone salsa como él.',
    tags: ['salsa', 'tropical', 'baile'],
  },
  {
    id: 'rc-locutor-12',
    nombre: 'Luis Fernando Gómez',
    ciudad: 'San José',
    estado: 'San José',
    pais: 'Costa Rica',
    paisCodigo: 'CR',
    avatarUrl: 'https://readdy.ai/api/search-image?query=Mature%20Costa%20Rican%20news%20anchor%20portrait%2C%20professional%20suit%2C%20distinguished%20look%2C%20trustworthy%20expression&width=160&height=160&seq=rc-loc-img-12&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Costa%20Rican%20news%20anchor%20in%20professional%20studio%2C%20broadcasting%20news%2C%20maps%20on%20screens%2C%20modern%20clean%20atmosphere%2C%20tropical%20daylight&width=800&height=500&seq=rc-loc-cover-12&orientation=landscape',
    radioNombre: 'Noticias CR Hoy',
    radioFrecuencia: '87.5 RFW',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Costa%20Rican%20news%20radio%20logo%20with%20globe%20and%20microphone%2C%20blue%20and%20white&width=80&height=80&seq=rc-loc-radio-12&orientation=squarish',
    distancia: 1.3,
    seguidores: 34500,
    estaEnVivo: true,
    especialidad: 'Periodismo y Actualidad',
    idioma: 'es',
    bio: 'Periodista de raza. Cubre la actualidad costarricense con rigor y profundidad. Su análisis político es referencia obligada en San José.',
    tags: ['noticias', 'periodismo', 'política'],
  },
  {
    id: 'rc-locutor-13',
    nombre: 'Diego Vargas',
    ciudad: 'CDMX',
    estado: 'Ciudad de México',
    pais: 'México',
    paisCodigo: 'MX',
    avatarUrl: 'https://readdy.ai/api/search-image?query=Young%20Mexican%20male%20radio%20host%20portrait%2C%20stylish%20modern%20look%2C%20confident%20expression%2C%20urban%20professional%20headshot&width=160&height=160&seq=rc-loc-img-13&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Young%20Mexican%20radio%20host%20in%20modern%20Mexico%20City%20studio%2C%20interviewing%20artist%2C%20cityscape%20through%20window%2C%20contemporary%20cool%20atmosphere&width=800&height=500&seq=rc-loc-cover-13&orientation=landscape',
    radioNombre: 'Estéreo Capital',
    radioFrecuencia: '102.3 RFW',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Modern%20urban%20radio%20station%20logo%20with%20Mexico%20City%20skyline%2C%20mustard%20yellow%20and%20charcoal&width=80&height=80&seq=rc-loc-radio-13&orientation=squarish',
    distancia: 2.5,
    seguidores: 56700,
    estaEnVivo: true,
    especialidad: 'Pop y Cultura Urbana',
    idioma: 'es',
    bio: 'La nueva generación de la radio mexicana. Pop, cultura urbana y las mejores entrevistas. Conecta con la juventud de CDMX como nadie.',
    tags: ['pop', 'urbano', 'entrevistas'],
  },
  {
    id: 'rc-locutor-14',
    nombre: 'Alberto Fernández',
    ciudad: 'Madrid',
    estado: 'Comunidad de Madrid',
    pais: 'España',
    paisCodigo: 'ES',
    avatarUrl: 'https://readdy.ai/api/search-image?query=Professional%20Spanish%20sports%20journalist%20portrait%2C%20confident%20expression%2C%20smart%20casual%20attire%2C%20experienced%20broadcaster&width=160&height=160&seq=rc-loc-img-14&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Spanish%20sports%20journalist%20in%20Madrid%20studio%2C%20commentating%20live%20match%2C%20Real%20Madrid%20memorabilia%2C%20professional%20broadcast%20environment&width=800&height=500&seq=rc-loc-cover-14&orientation=landscape',
    radioNombre: 'Radio Deportes Madrid',
    radioFrecuencia: '100.6 RFW',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Spanish%20sports%20radio%20logo%20with%20Real%20Madrid%20crown%20motif%2C%20white%20and%20gold&width=80&height=80&seq=rc-loc-radio-14&orientation=squarish',
    distancia: 3.6,
    seguidores: 72300,
    estaEnVivo: true,
    especialidad: 'Fútbol Español',
    idioma: 'es',
    bio: 'El referente del periodismo deportivo madrileño. Sigue al Real Madrid y Atlético con pasión y objetividad. Su análisis es citado en medios nacionales.',
    tags: ['fútbol', 'La Liga', 'análisis'],
  },
  {
    id: 'rc-locutor-15',
    nombre: 'Ana Lucía Hernández',
    ciudad: 'San José',
    estado: 'San José',
    pais: 'Costa Rica',
    paisCodigo: 'CR',
    avatarUrl: 'https://readdy.ai/api/search-image?query=Warm%20Costa%20Rican%20community%20radio%20volunteer%20portrait%2C%20genuine%20kind%20smile%2C%20casual%20style%2C%20approachable%20personality&width=160&height=160&seq=rc-loc-img-15&orientation=squarish',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Costa%20Rican%20community%20radio%20host%20in%20intimate%20neighborhood%20studio%2C%20local%20art%20on%20walls%2C%20diverse%20team%2C%20warm%20authentic%20grassroots%20atmosphere&width=800&height=500&seq=rc-loc-cover-15&orientation=landscape',
    radioNombre: 'Radio Comunitaria CR',
    radioFrecuencia: '160.00 RFW',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Community%20radio%20station%20logo%20with%20people%20holding%20hands%2C%20warm%20earth%20tones%2C%20grassroots%20broadcast%20emblem&width=80&height=80&seq=rc-loc-radio-15&orientation=squarish',
    distancia: 2.2,
    seguidores: 12300,
    estaEnVivo: false,
    especialidad: 'Comunicación Comunitaria',
    idioma: 'es',
    bio: 'Activista comunitaria y radialista. Da voz a los barrios de San José. Su programa conecta vecinos, resuelve problemas y celebra la diversidad tica.',
    tags: ['comunitaria', 'local', 'participativa'],
  },
];

// ─── Stats Globales ───

export const statsRadiosCercanas = {
  totalRadios: radiosCercanas.length,
  totalEventos: eventosCercanos.length,
  totalLocutores: locutoresCercanos.length,
  ubicacionesCubiertas: ubicacionesDetectables.length,
  radioMasCercana: 'Radio Pura Vida (0.5 km)',
  eventoMasGrande: 'Madrid Suena (15,000 participantes)',
  locutorMasSeguido: 'Martín Castellanos (125,000 seguidores)',
};

// ─── Helpers ───

export function formatearNumero(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

export function getRadiosPorUbicacion(paisCodigo: string, ciudad?: string): RadioCercana[] {
  return radiosCercanas
    .filter(r => r.paisCodigo === paisCodigo && (!ciudad || r.ciudad === ciudad))
    .sort((a, b) => a.distancia - b.distancia);
}

export function getEventosPorUbicacion(paisCodigo: string, ciudad?: string): EventoCercano[] {
  return eventosCercanos
    .filter(e => e.paisCodigo === paisCodigo && (!ciudad || e.ciudad === ciudad))
    .sort((a, b) => a.distancia - b.distancia);
}

export function getLocutoresPorUbicacion(paisCodigo: string, ciudad?: string): LocutorCercano[] {
  return locutoresCercanos
    .filter(l => l.paisCodigo === paisCodigo && (!ciudad || l.ciudad === ciudad))
    .sort((a, b) => a.distancia - b.distancia);
}

export function getRadiosCercanasConFiltros(
  paisCodigo: string,
  ciudad?: string,
  distanciaMax?: number,
  idioma?: string,
  categoria?: string
): RadioCercana[] {
  let result = getRadiosPorUbicacion(paisCodigo, ciudad);
  if (distanciaMax !== undefined) result = result.filter(r => r.distancia <= distanciaMax);
  if (idioma && idioma !== 'todos') result = result.filter(r => r.idioma === idioma);
  if (categoria && categoria !== 'todas') result = result.filter(r => r.categoria === categoria);
  return result;
}

export function getEventosCercanosConFiltros(
  paisCodigo: string,
  ciudad?: string,
  distanciaMax?: number
): EventoCercano[] {
  let result = getEventosPorUbicacion(paisCodigo, ciudad);
  if (distanciaMax !== undefined) result = result.filter(e => e.distancia <= distanciaMax);
  return result;
}

export function getLocutoresCercanosConFiltros(
  paisCodigo: string,
  ciudad?: string,
  distanciaMax?: number,
  idioma?: string
): LocutorCercano[] {
  let result = getLocutoresPorUbicacion(paisCodigo, ciudad);
  if (distanciaMax !== undefined) result = result.filter(l => l.distancia <= distanciaMax);
  if (idioma && idioma !== 'todos') result = result.filter(l => l.idioma === idioma);
  return result;
}

export function getRadiosEnVivoCercanas(paisCodigo: string): RadioCercana[] {
  return radiosCercanas.filter(r => r.estaEnVivo && r.paisCodigo === paisCodigo).sort((a, b) => a.distancia - b.distancia);
}

export function getRadiosTendenciaCercanas(paisCodigo: string): RadioCercana[] {
  return radiosCercanas.filter(r => r.enTendencia && r.paisCodigo === paisCodigo).sort((a, b) => a.distancia - b.distancia);
}